// src/components/settings/SettingsPage.js
import { useState, useEffect } from "react";
import Icon from "../icons/Icon";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { PERMISSIONS } from "../../config/permissions";
import "../../styles/components/settings-hub.css";

const SettingsPage = () => {
  const { company, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const basePath = `/company/${company?.slug}/settings`;

  const isActive = (subPath) => location.pathname.includes(subPath);

  const handleNav = (subPath) => {
    navigate(`${basePath}/${subPath}`);
  };

  return (
    <div className="settings-hub">
      {/* Settings Sub-Sidebar — desktop only */}
      {!isMobile && (
        <nav className="settings-hub-sidebar">
          <div className="settings-hub-nav-section">
            <div className="settings-hub-nav-title">Company</div>
            {(hasPermission(PERMISSIONS.SETTINGS_DEPARTMENTS) || hasPermission(PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW)) && (
              <a
                href={`${basePath}/departments`}
                className={`settings-hub-nav-link ${isActive("/departments") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("departments");
                }}
              >
                <Icon name="grid" />
                Departments
              </a>
            )}
            {hasPermission(PERMISSIONS.SETTINGS_TEAM_VIEW) && (
              <a
                href={`${basePath}/team`}
                className={`settings-hub-nav-link ${isActive("/team") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("team");
                }}
              >
                <Icon name="users" />
                Team
              </a>
            )}
            {(hasPermission(PERMISSIONS.SETTINGS_ROLES_EDIT) || hasPermission(PERMISSIONS.SETTINGS_ROLES_VIEW)) && (
              <a
                href={`${basePath}/roles`}
                className={`settings-hub-nav-link ${isActive("/roles") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("roles");
                }}
              >
                <Icon name="star" />
                Roles
              </a>
            )}
          {(hasPermission(PERMISSIONS.SETTINGS_ATTENDANCE_POLICY) || hasPermission(PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW)) && (
              <a
                href={`${basePath}/attendance-policy`}
                className={`settings-hub-nav-link ${isActive("/attendance-policy") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("attendance-policy");
                }}
              >
                <Icon name="clock" />
                Attendance Policy
              </a>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_HOLIDAYS) || hasPermission(PERMISSIONS.SETTINGS_HOLIDAYS_VIEW)) && (
              <a
                href={`${basePath}/holidays`}
                className={`settings-hub-nav-link ${isActive("/settings/holidays") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("holidays");
                }}
              >
                <Icon name="calendar" />
                Holiday Calendar
              </a>
          )}
          </div>

          {(hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES) || hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW) || hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT) || hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW) || hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS) || hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS_VIEW)) && (
            <div className="settings-hub-nav-section">
              <div className="settings-hub-nav-title">Payroll</div>
              {(hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES) || hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW)) && (
              <a
                href={`${basePath}/payroll-rates`}
                className={`settings-hub-nav-link ${isActive("/payroll-rates") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("payroll-rates");
                }}
              >
                <Icon name="dollar-sign" />
                Pay Rates
              </a>
              )}
              {(hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT) || hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW)) && (
              <a
                href={`${basePath}/csv-export`}
                className={`settings-hub-nav-link ${isActive("/csv-export") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("csv-export");
                }}
              >
                <Icon name="file-text" />
                CSV Export Layout
              </a>
              )}
              {(hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS) || hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS_VIEW)) && (
              <a
                href={`${basePath}/payslips`}
                className={`settings-hub-nav-link ${isActive("/payslips") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("payslips");
                }}
              >
                <Icon name="file-text" />
                Payslips
              </a>
              )}
            </div>
          )}

          {hasPermission(PERMISSIONS.SETTINGS_PAYMENTS) && (
            <div className="settings-hub-nav-section">
              <div className="settings-hub-nav-title">Billing</div>
              <a
                href={`${basePath}/subscriptions`}
                className={`settings-hub-nav-link ${isActive("/subscriptions") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("subscriptions");
                }}
              >
                <Icon name="credit-card" />
                Subscriptions
              </a>
              <a
                href={`${basePath}/invoices`}
                className={`settings-hub-nav-link ${isActive("/invoices") ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("invoices");
                }}
              >
                <Icon name="file-text" />
                Invoice History
              </a>
            </div>
          )}

        </nav>
      )}

      {/* Mobile tab bar */}
      {isMobile && (
        <div className="settings-hub-mobile-nav">
          {(hasPermission(PERMISSIONS.SETTINGS_DEPARTMENTS) || hasPermission(PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/departments") ? "active" : ""}`}
              onClick={() => handleNav("departments")}
            >
              Departments
            </button>
          )}
          {hasPermission(PERMISSIONS.SETTINGS_TEAM_VIEW) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/team") ? "active" : ""}`}
              onClick={() => handleNav("team")}
            >
              Team
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_ROLES_EDIT) || hasPermission(PERMISSIONS.SETTINGS_ROLES_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/roles") ? "active" : ""}`}
              onClick={() => handleNav("roles")}
            >
              Roles
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_ATTENDANCE_POLICY) || hasPermission(PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/attendance-policy") ? "active" : ""}`}
              onClick={() => handleNav("attendance-policy")}
            >
              Attendance
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_HOLIDAYS) || hasPermission(PERMISSIONS.SETTINGS_HOLIDAYS_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/settings/holidays") ? "active" : ""}`}
              onClick={() => handleNav("holidays")}
            >
              Holidays
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES) || hasPermission(PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/payroll-rates") ? "active" : ""}`}
              onClick={() => handleNav("payroll-rates")}
            >
              Pay Rates
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT) || hasPermission(PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/csv-export") ? "active" : ""}`}
              onClick={() => handleNav("csv-export")}
            >
              CSV Export
            </button>
          )}
          {(hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS) || hasPermission(PERMISSIONS.SETTINGS_PAYSLIPS_VIEW)) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/payslips") ? "active" : ""}`}
              onClick={() => handleNav("payslips")}
            >
              Payslips
            </button>
          )}
          {hasPermission(PERMISSIONS.SETTINGS_PAYMENTS) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/subscriptions") ? "active" : ""}`}
              onClick={() => handleNav("subscriptions")}
            >
              Subscriptions
            </button>
          )}
          {hasPermission(PERMISSIONS.SETTINGS_PAYMENTS) && (
            <button
              className={`settings-hub-mobile-tab ${isActive("/invoices") ? "active" : ""}`}
              onClick={() => handleNav("invoices")}
            >
              Invoice History
            </button>
          )}
        </div>
      )}

      {/* Settings Content Area */}
      <div className="settings-hub-content">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsPage;
